import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { API_BASE } from '../config';

const PUSH_TOKEN_STORAGE_KEY = 'wbf_push_token';
const PUSH_LAST_TEST_STORAGE_KEY = 'wbf_push_last_test';
const PUSH_LAST_ERROR_STORAGE_KEY = 'wbf_push_last_error';
let firebaseAppSingleton = null;

function setLastPushError(errorPayload) {
  if (typeof window === 'undefined') return;
  try {
    const payload =
      typeof errorPayload === 'string'
        ? { message: errorPayload }
        : { ...errorPayload };
    localStorage.setItem(PUSH_LAST_ERROR_STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    console.error('[push] Failed to persist last push error', e);
  }
}

function clearLastPushError() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(PUSH_LAST_ERROR_STORAGE_KEY);
}

function normalizeErrorForUi(error, fallbackReason = 'unknown_error') {
  if (!error) return { reason: fallbackReason, message: 'Unknown push error', code: null };
  if (typeof error === 'string') return { reason: fallbackReason, message: error, code: null };
  return {
    reason: error.reason || fallbackReason,
    message: error.message || String(error),
    code: error.code || null,
    stack: error.stack || null
  };
}

function firebaseConfigFromEnv() {
  const config = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
  };
  const missing = Object.entries(config).filter(([, v]) => !v).map(([k]) => k);
  return { config, missing };
}

function getOrInitFirebaseApp() {
  const { config, missing } = firebaseConfigFromEnv();
  if (missing.length > 0) {
    console.warn('[push] Missing Firebase env values:', missing.join(', '));
    return null;
  }
  if (firebaseAppSingleton) return firebaseAppSingleton;
  if (getApps().length > 0) {
    firebaseAppSingleton = getApps()[0];
    return firebaseAppSingleton;
  }
  firebaseAppSingleton = initializeApp(config);
  return firebaseAppSingleton;
}

async function registerPushTokenOnBackend(jwtToken, fcmToken) {
  console.log('[push] Saving token to backend /api/notifications/push-token');
  const res = await fetch(`${API_BASE}/api/notifications/push-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwtToken}`
    },
    body: JSON.stringify({
      token: fcmToken,
      platform: 'web',
      deviceId: `${navigator.platform || 'web'}-${navigator.userAgent || ''}`.slice(0, 120),
      appVersion: process.env.REACT_APP_VERSION || 'web',
      clientOrigin: typeof window !== 'undefined' ? window.location.origin : ''
    })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    console.error('[push] Backend token save failed', { status: res.status, data });
    throw new Error(data.error || `push-token register failed (${res.status})`);
  }
  const payload = await res.json().catch(() => ({}));
  console.log('[push] Backend token save response', payload);
  return payload;
}

async function runPushTestOnBackend(jwtToken) {
  const res = await fetch(`${API_BASE}/api/notifications/push-test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwtToken}`
    }
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `push-test failed (${res.status})`);
  }
  return data;
}

function isIOSDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isPwaStandalone() {
  if (typeof window === 'undefined') return false;
  // iOS Safari
  // eslint-disable-next-line no-undef
  if (window.navigator && window.navigator.standalone) return true;
  return window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
}

export function getStoredWebPushToken() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
}

export function getLastPushTestResult() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PUSH_LAST_TEST_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.error('[push] Failed to parse last push-test result from localStorage', e);
    return null;
  }
}

export function getLastPushError() {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(PUSH_LAST_ERROR_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_e) {
    return { message: raw };
  }
}

export function isVapidKeyConfigured() {
  return !!process.env.REACT_APP_FIREBASE_VAPID_KEY;
}

export async function setupWebPushForCurrentUser(jwtToken, options = {}) {
  console.group('[push] setupWebPushForCurrentUser');
  try {
    clearLastPushError();
    if (!jwtToken || typeof window === 'undefined') {
      const err = { reason: 'no_jwt', message: 'Missing JWT or window context' };
      console.error('[push] Missing JWT or window context');
      setLastPushError(err);
      return { ok: false, ...err };
    }
    if (!('Notification' in window)) {
      const err = { reason: 'notifications_unsupported', message: 'Notification API not supported' };
      console.error('[push] Notification API not supported');
      setLastPushError(err);
      return { ok: false, ...err };
    }
    if (!('serviceWorker' in navigator)) {
      const err = { reason: 'sw_unsupported', message: 'Service workers are not supported' };
      console.error('[push] Service workers are not supported');
      setLastPushError(err);
      return { ok: false, ...err };
    }

    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '[::1]';
    const secureAllowed = window.isSecureContext || isLocalhost;
    console.log('[push] Runtime context', {
      origin: window.location.origin,
      secureContext: window.isSecureContext,
      localhost: isLocalhost
    });
    if (!secureAllowed) {
      const err = {
        reason: 'insecure_context',
        message: 'Insecure context detected. Web push requires HTTPS (except localhost).'
      };
      console.error('[push] Insecure context detected. Web push requires HTTPS (except localhost).');
      setLastPushError(err);
      return { ok: false, ...err };
    }

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    console.log('[push] Production env validation: VAPID key exists:', !!vapidKey);
    console.log('[push] VAPID key exists:', !!vapidKey);
    if (!vapidKey) {
      const err = {
        reason: 'missing_vapid_key',
        message: 'Missing REACT_APP_FIREBASE_VAPID_KEY in current build environment'
      };
      console.error('[push] Missing REACT_APP_FIREBASE_VAPID_KEY');
      setLastPushError(err);
      return { ok: false, ...err };
    }

    const supported = await isSupported().catch((err) => {
      console.error('[push] firebase.messaging isSupported() failed', err);
      return false;
    });
    console.log('[push] Firebase messaging supported:', supported);
    if (!supported) {
      const err = {
        reason: 'messaging_not_supported',
        message: 'Firebase messaging is not supported in this browser/device'
      };
      setLastPushError(err);
      return { ok: false, ...err };
    }

    const permission = await Notification.requestPermission();
    console.log('[push] Notification permission:', permission);
    if (permission !== 'granted') {
      const err = { reason: 'permission_denied', message: 'Notification permission is not granted' };
      console.error('[push] Notification permission is not granted');
      setLastPushError(err);
      return { ok: false, ...err };
    }

    const app = getOrInitFirebaseApp();
    if (!app) {
      const err = { reason: 'missing_firebase_config', message: 'Firebase app initialization failed' };
      console.error('[push] Firebase app initialization failed');
      setLastPushError(err);
      return { ok: false, ...err };
    }
    console.log('[push] Firebase app initialized:', !!app);

    // Register service worker first, then wait for readiness before token generation.
    const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    if (options.forceRefresh && swRegistration.update) {
      await swRegistration.update().catch((e) => {
        console.warn('[push] SW update failed during force refresh', e);
      });
    }
    await navigator.serviceWorker.ready;
    console.log('[push] SW registration scope:', swRegistration?.scope || '(none)');

    const messaging = getMessaging(app);
    const fcmToken = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swRegistration
    });
    console.log('[push] FCM token result:', fcmToken || '(empty)');
    if (!fcmToken) {
      const err = {
        reason: 'empty_fcm_token',
        message: 'FCM token empty'
      };
      console.error('[push] getToken returned empty token');
      setLastPushError(err);
      return { ok: false, ...err };
    }

    const previousToken = localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
    if (previousToken === fcmToken) {
      console.log('[push] Token unchanged; backend update skipped');
      return { ok: true, reused: true };
    }

    await registerPushTokenOnBackend(jwtToken, fcmToken);
    console.log('[push] Token saved to backend successfully');
    localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, fcmToken);
    clearLastPushError();
    return { ok: true, reused: false };
  } catch (error) {
    const normalized = normalizeErrorForUi(
      {
        reason: 'fcm_setup_failed',
        code: error?.code || null,
        message: error?.message || 'FCM setup failed',
        stack: error?.stack || null
      },
      'fcm_setup_failed'
    );
    console.error('[push] FCM setup error', {
      code: normalized.code,
      message: normalized.message,
      stack: normalized.stack
    });
    setLastPushError(normalized);
    return {
      ok: false,
      reason: normalized.reason,
      message: normalized.message,
      code: normalized.code
    };
  } finally {
    console.groupEnd();
  }
}

/**
 * Stable Web Push enablement flow (must be called from a user click).
 * Steps: permission -> SW -> FCM token -> save backend -> push-test -> return status.
 */
export async function enableWebPushAndTest(jwtToken) {
  clearLastPushError();
  try {
    if (isIOSDevice() && !isPwaStandalone()) {
      return {
        ok: false,
        reason: 'ios_requires_pwa',
        message: 'To receive notifications outside the browser, install this website to Home Screen.'
      };
    }

    const setup = await setupWebPushForCurrentUser(jwtToken);
    if (!setup.ok) {
      const reason = setup.reason;
      const messageByReason = {
        no_jwt: 'You must be logged in.',
        notifications_unsupported: 'This browser does not support notifications.',
        sw_unsupported: 'Service workers are not supported in this browser.',
        missing_vapid_key: 'Missing VAPID key configuration (frontend env).',
        messaging_not_supported: 'Firebase messaging is not supported in this browser/device.',
        permission_denied: 'Notification permission was denied. Enable it in browser settings and try again.',
        missing_firebase_config: 'Missing Firebase configuration (frontend env).',
        empty_fcm_token: 'FCM token could not be generated. Please retry.'
      };
      return { ...setup, message: setup.message || messageByReason[reason] || 'Unable to enable notifications.' };
    }

    const test = await runPushTestOnBackend(jwtToken);
    localStorage.setItem(PUSH_LAST_TEST_STORAGE_KEY, JSON.stringify({ at: Date.now(), ...test }));
    return { ok: true, setup, test };
  } catch (e) {
    console.error('[push] enableWebPushAndTest failed', e);
    const msg = e && e.message ? e.message : 'Push setup failed';
    const err = normalizeErrorForUi({ reason: 'exception', message: msg, code: e?.code || null }, 'exception');
    setLastPushError(err);
    return { ok: false, reason: err.reason, message: err.message, code: err.code };
  }
}

export async function regenerateWebPushToken(jwtToken) {
  clearLastPushError();
  if (!jwtToken) {
    const err = { reason: 'no_jwt', message: 'Missing auth token' };
    setLastPushError(err);
    return { ok: false, ...err };
  }

  const oldToken = localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
  if (oldToken) {
    try {
      await fetch(`${API_BASE}/api/notifications/push-token`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwtToken}`
        },
        body: JSON.stringify({ token: oldToken })
      });
    } catch (e) {
      console.warn('[push] Could not delete previous token before regeneration', e);
    }
  }

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      const targets = regs.filter((r) => r?.active?.scriptURL?.includes('firebase-messaging-sw.js'));
      for (const reg of targets) {
        await reg.unregister();
      }
    }
  } catch (e) {
    console.warn('[push] Could not unregister previous firebase messaging service worker', e);
  }

  localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
  const setup = await setupWebPushForCurrentUser(jwtToken, { forceRefresh: true });
  if (!setup.ok) return setup;
  return setup;
}

export async function fetchBackendPushStatus(jwtToken) {
  if (!jwtToken) return null;
  const res = await fetch(`${API_BASE}/api/notifications/push-status`, {
    headers: { Authorization: `Bearer ${jwtToken}` }
  });
  return res.json().catch((e) => {
    console.error('[push] Failed to parse /push-status response JSON', e);
    return {};
  });
}

export async function getWebPushDebugSnapshot(jwtToken) {
  const permission = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
  let sw = { supported: false };
  try {
    sw.supported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
    if (sw.supported) {
      const reg = await navigator.serviceWorker.getRegistration('/');
      sw.registered = !!reg;
      sw.active = !!reg?.active;
      sw.scope = reg?.scope || null;
      sw.scriptURL = reg?.active?.scriptURL || null;
    }
  } catch (e) {
    console.error('[push] Failed to inspect service worker registration', e);
    sw = { supported: true, error: 'getRegistration_failed' };
  }

  const storedToken = getStoredWebPushToken();
  const backend = await fetchBackendPushStatus(jwtToken).catch((e) => {
    console.error('[push] Failed to fetch backend push status', e);
    return null;
  });
  const lastTest = getLastPushTestResult();
  const lastError = getLastPushError();

  return {
    permission,
    vapidKeyExists: isVapidKeyConfigured(),
    serviceWorker: sw,
    storedTokenPresent: !!storedToken,
    storedTokenPreview: storedToken ? `${storedToken.slice(0, 10)}…${storedToken.slice(-8)}` : null,
    backend,
    lastTest,
    lastError,
    ios: isIOSDevice(),
    standalone: isPwaStandalone()
  };
}

export async function unregisterWebPushForCurrentUser(jwtToken) {
  if (!jwtToken) return;
  const token = localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
  if (!token) return;
  try {
    await fetch(`${API_BASE}/api/notifications/push-token`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`
      },
      body: JSON.stringify({ token })
    });
  } catch (e) {
    console.error('[push] Failed to unregister push token on logout', e);
  } finally {
    localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(PUSH_LAST_TEST_STORAGE_KEY);
    localStorage.removeItem(PUSH_LAST_ERROR_STORAGE_KEY);
  }
}
